/**
 * Worker Runtime V1 — Platform Admin "Worker 节点" page (spec §53-§64).
 *
 * Kept deliberately minimal per spec §54: Summary + Worker table + detail
 * drawer + Enable/Drain/Disable. No task-trend charts, no capability
 * distribution charts, no node-grouping stats (spec §54 explicitly defers
 * these). "Restart" is NOT_IMPLEMENTED (spec §60) — not shown at all.
 */
import { ClusterOutlined, KeyOutlined, PauseCircleOutlined, PoweroffOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Checkbox, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Table, Tag, Tooltip, Typography, message } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  platformCreateEnrollmentToken,
  platformDisableWorker,
  platformDrainWorker,
  platformEnableWorker,
  platformArchiveWorker,
  platformCreateWorkerTestJob,
  platformGetTaskDetail,
  platformGetWorker,
  platformGetWorkerCapabilityMatrix,
  platformGetWorkerDeletionEligibility,
  platformListEnrollmentTokens,
  platformListWorkerEngines,
  platformListWorkers,
  platformRevokeEnrollmentToken,
  platformSetWorkerCapabilityAssignment,
  platformSetWorkerDesiredState,
  platformSetWorkerEngineDesiredState,
} from '../api'
import type {
  AdminWorkerRow,
  EnrollmentTokenRow,
  PlatformTaskDetail,
  WorkerCapabilityMatrixResponse,
  WorkerDirectTestCreated,
  WorkerCapabilityMatrixWorker,
  WorkerProcessDesiredState,
  WorkerProcessLifecycleOverlay,
  WorkerDeletionEligibility,
  WorkerDetail,
  WorkerEngineLifecycleSummary,
  WorkerEngineRow,
  WorkersPageResponse,
} from '../types'
import { SectionCard, SummaryCard } from './adminShared'
import { extractDirectTestResultSummary } from './directTestResultSummary'
import { cardIconBg, platformColors } from './theme'
import {
  desiredStateLabel,
  engineActualStateTone,
  engineControlDisabled,
  engineControlLoading,
  engineReadinessIsError,
  engineReadinessIsIdle,
  engineReadinessLabel,
  formatEngineControlLabel,
  formatRuntimeUpgradeDetail,
  inferGpuEngineLabel,
  runtimeUpgradeBadgeMeta,
  summarizeActiveEngine,
} from './workerEngineObservability'

/** Same cadence as the Overview page (spec §102: "5s or 10s polling"). */
const AUTO_REFRESH_MS = 10_000
/** Platform Admin Density & Worker Liveness P0 (item 十八) — this page
 * previously used a naive `.catch(err => setError(...))` with no
 * consecutive-failure guard, unlike `OverviewPage.tsx` (spec §18: "last
 * successful snapshot" awareness must be consistent across surfaces).
 * Same threshold/reasoning as Overview's own constant. */
const CONSECUTIVE_FAILURES_BEFORE_BANNER = 2

function lifecycleRuntimeLabel(
  state: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const map: Record<string, string> = {
    ONLINE: 'workers.lifecycle.runtimeOnline',
    STARTING: 'workers.lifecycle.runtimeStarting',
    IDLE: 'workers.lifecycle.runtimeIdle',
    BUSY: 'workers.lifecycle.runtimeBusy',
    STOPPING: 'workers.lifecycle.runtimeStopping',
    OFFLINE: 'workers.lifecycle.runtimeOffline',
    ERROR: 'workers.lifecycle.runtimeError',
    UNKNOWN: 'workers.lifecycle.runtimeUnknown',
  }
  return t(map[state || 'UNKNOWN'] || 'workers.lifecycle.runtimeUnknown')
}

function LifecycleRuntimePill({ lifecycle, t }: { lifecycle?: WorkerProcessLifecycleOverlay | null; t: (key: string) => string }) {
  if (!lifecycle) return <span style={{ color: platformColors.secondaryText }}>—</span>
  const state = lifecycle.runtime_state
  const tone =
    state === 'ERROR'
      ? { bg: platformColors.dangerBg, color: platformColors.danger }
      : state === 'STARTING' || state === 'STOPPING'
        ? { bg: platformColors.warningBg, color: platformColors.warning }
        : state === 'OFFLINE' || state === 'UNKNOWN'
          ? { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText }
          : { bg: platformColors.successBg, color: platformColors.success }
  const title = [
    lifecycle.wake ? t('workers.lifecycle.wakePending') : null,
    lifecycle.last_error_code ? `${t('workers.lifecycle.lastError')}: ${lifecycle.last_error_code}` : null,
    lifecycle.host_online === false ? t('workers.lifecycle.hostOffline') : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <Tooltip title={title || undefined}>
      <StatusPill label={lifecycleRuntimeLabel(state, t)} tone={tone} title={title || lifecycleRuntimeLabel(state, t)} />
    </Tooltip>
  )
}

function LifecycleDesiredControl({
  row,
  actioningId,
  onChanged,
  t,
}: {
  row: AdminWorkerRow
  actioningId: string | null
  onChanged: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const lifecycle = row.lifecycle
  const desired = (lifecycle?.desired_state || (row.status === 'disabled' ? 'OFFLINE' : row.status === 'draining' ? 'DRAINING' : 'ONLINE')) as WorkerProcessDesiredState
  const hostOffline = lifecycle?.host_online === false
  const busy = actioningId === row.worker_id
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
      <Segmented
        size="small"
        disabled={busy}
        value={desired}
        options={[
          { label: t('workers.lifecycle.online'), value: 'ONLINE', disabled: hostOffline && desired !== 'ONLINE' },
          { label: t('workers.lifecycle.auto'), value: 'AUTO', disabled: hostOffline && desired !== 'AUTO' },
          { label: t('workers.lifecycle.draining'), value: 'DRAINING' },
          { label: t('workers.lifecycle.offline'), value: 'OFFLINE' },
        ]}
        onChange={async (value) => {
          const next = String(value) as WorkerProcessDesiredState
          if (next === desired) return
          if (hostOffline && (next === 'ONLINE' || next === 'AUTO')) {
            message.warning(t('workers.lifecycle.hostOfflineDisabled'))
            return
          }
          try {
            await platformSetWorkerDesiredState(row.worker_id, next)
            message.success(t('workers.lifecycle.setSuccess'))
            onChanged()
          } catch {
            message.error(t('workers.lifecycle.setFailed'))
          }
        }}
      />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <LifecycleRuntimePill lifecycle={lifecycle} t={t} />
        {hostOffline ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('workers.lifecycle.hostOfflineDisabled')}
          </Typography.Text>
        ) : null}
        {lifecycle?.last_error_code ? (
          <Typography.Text type="danger" style={{ fontSize: 11 }}>
            {lifecycle.last_error_code}
          </Typography.Text>
        ) : null}
      </div>
    </div>
  )
}

function formatRelativeTime(iso: string | null, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const diffSeconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (diffSeconds < 60) return t('workers.heartbeat.secondsAgo', { count: diffSeconds })
  const diffMinutes = Math.round(diffSeconds / 60)
  if (diffMinutes < 60) return t('workers.heartbeat.minutesAgo', { count: diffMinutes })
  const diffHours = Math.round(diffMinutes / 60)
  if (diffHours < 24) return t('workers.heartbeat.hoursAgo', { count: diffHours })
  const diffDays = Math.round(diffHours / 24)
  return t('workers.heartbeat.daysAgo', { count: diffDays })
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('zh-CN', { hour12: false })
}

/** Worker Node Admin UI V1.1 — the single `Worker.status` field the
 * backend already exposes actually carries TWO independent real-world
 * concepts (see `WorkerRegistryService.set_control_state`/`heartbeat`):
 *
 * 1. 运行状态 (liveness, self-reported/heartbeat-derived) — is the
 *    process actually alive right now? Purely a function of `online`
 *    (heartbeat recency) plus the runtime's own `busy` self-report.
 * 2. 接单状态 (admin-controlled accept-job gate) — is a Platform Admin
 *    currently *allowing* this Worker to claim new Jobs? Purely a
 *    function of the admin-set `status` value (`online` = accepting /
 *    `draining` = draining / `disabled` = disabled), independent of
 *    whether the process happens to be alive right now.
 *
 * The previous UI collapsed both into one `WorkerStatusBadge` (Online/
 * Busy/Offline/Draining/Disabled) plus a parenthetical `ControlStateHint`
 * — admins reported this as confusing ("Online" looked like "will accept
 * work" when it only ever meant "has a heartbeat"). These two small pills
 * replace both call sites in this page ONLY (`OverviewPage.tsx`'s own use
 * of the shared `WorkerStatusBadge` is untouched — out of scope this
 * round) with two independently-computed, always-visible values — never
 * hiding one axis behind the other. */
function StatusPill({ label, tone, title }: { label: string; tone: { bg: string; color: string }; title?: string }) {
  const pill = (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 4,
        background: tone.bg,
        color: tone.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill
}

/** 运行状态 — pure Worker runtime liveness (heartbeat only). Task busy state
 * is shown separately in the「当前任务」column — never folded into this pill. */
function RunningStatusPill({
  online,
  heartbeatAgeSeconds,
  t,
}: {
  online: boolean
  heartbeatAgeSeconds?: number | null
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  if (!online) {
    const staleHint =
      heartbeatAgeSeconds != null && heartbeatAgeSeconds > 60
        ? t('workers.heartbeat.staleTooltip', { seconds: Math.round(heartbeatAgeSeconds) })
        : t('workers.workerRuntime.offline')
    return (
      <StatusPill
        label={heartbeatAgeSeconds != null && heartbeatAgeSeconds > 60 ? t('workers.workerRuntime.stale') : t('workers.workerRuntime.offline')}
        tone={{ bg: platformColors.dangerBg, color: platformColors.danger }}
        title={staleHint}
      />
    )
  }
  return (
    <StatusPill
      label={t('workers.workerRuntime.online')}
      tone={{ bg: platformColors.successBg, color: platformColors.success }}
      title={t('workers.workerRuntime.online')}
    />
  )
}

function TaskStatePill({
  row,
  t,
}: {
  row: AdminWorkerRow
  t: (key: string) => string
}) {
  const hasTask = (row.current_tasks?.length ?? 0) > 0 || !!row.current_task || row.status === 'busy'
  if (!hasTask) {
    return <StatusPill label={t('workers.task.idle')} tone={{ bg: 'var(--pf-neutral-bg)', color: platformColors.mutedText }} />
  }
  return <StatusPill label={t('workers.task.running')} tone={{ bg: platformColors.infoBg, color: platformColors.info }} />
}

type AcceptingState = 'accepting' | 'draining' | 'disabled' | 'waiting_online'

/** 接单状态 — pure admin control state, independent of liveness, with one
 * derived exception: an admin-enabled Worker that happens to be offline
 * cannot actually claim anything right now either, so it is surfaced as
 * "等待上线" (spec §8) rather than falsely claiming "接单中". This is a
 * *display* derivation only — it changes no backend value and the real
 * claim gate (`JobService.claim_for_worker`) already independently
 * requires both liveness-independent admin state AND the Worker actually
 * polling in to claim anything. */
function acceptingState(status: string, online: boolean): AcceptingState {
  if (status === 'disabled') return 'disabled'
  if (status === 'draining') return 'draining'
  return online ? 'accepting' : 'waiting_online'
}

function AcceptingStatusPill({ status, online, t }: { status: string; online: boolean; t: (key: string) => string }) {
  const state = acceptingState(status, online)
  if (state === 'disabled') {
    return <StatusPill label={t('workers.admission.disabled')} tone={{ bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText }} title={t('workers.admission.disabled')} />
  }
  if (state === 'draining') {
    return <StatusPill label={t('workers.admission.draining')} tone={{ bg: platformColors.warningBg, color: platformColors.warning }} title={t('workers.admission.draining')} />
  }
  if (state === 'waiting_online') {
    return <StatusPill label={t('workers.admission.waitingOnline')} tone={{ bg: 'var(--pf-neutral-bg)', color: platformColors.mutedText }} title={t('workers.admission.waitingOnline')} />
  }
  return <StatusPill label={t('workers.admission.accepting')} tone={{ bg: platformColors.successBg, color: platformColors.success }} title={t('workers.admission.accepting')} />
}

/** PC2 Unified Worker Productionization P0 — human labels for the fixed
 * pool names `ResourceManager` reports (`app.remote_worker.resources`).
 * An unknown pool name (future capability) falls back to itself. */
const POOL_LABEL: Record<string, string> = {
  video_gpu: 'Video GPU',
  image_gpu: 'Image GPU',
  cpu: 'CPU',
  deepzoom: 'DeepZoom',
}

function ResourcePoolsSummary({ snapshot }: { snapshot: AdminWorkerRow['resource_snapshot'] }) {
  if (!snapshot || !snapshot.pools || Object.keys(snapshot.pools).length === 0) return <span>—</span>
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {Object.entries(snapshot.pools).map(([name, pool]) => (
        <Tag key={name} color={pool.in_use > 0 ? 'processing' : 'default'}>
          {POOL_LABEL[name] ?? name}: {pool.in_use}/{pool.capacity}
        </Tag>
      ))}
      {snapshot.gpu_free_vram_mb != null && (
        <Tag>GPU 空闲显存: {(snapshot.gpu_free_vram_mb / 1024).toFixed(1)}GB</Tag>
      )}
    </div>
  )
}

const RUNTIME_COMPONENT_LABELS: Record<string, string> = {
  python_worker: 'Python Worker',
  browser_ai: 'Browser AI',
  chrome_cdp: 'Chrome CDP',
}

function RuntimeComponentsSummary({
  components,
}: {
  components: AdminWorkerRow['runtime_components']
}) {
  if (!components || Object.keys(components).length === 0) return <span>—</span>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Object.entries(components).map(([key, slice]) => {
        if (!slice) return null
        const healthy = slice.healthy
        return (
          <div key={key} style={{ border: '1px solid var(--pf-border)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{RUNTIME_COMPONENT_LABELS[key] ?? key}</div>
            {slice.source_sha && <div>SHA: {slice.source_sha}</div>}
            {healthy != null && (
              <div>
                状态:{' '}
                <StatusPill
                  label={healthy ? '健康' : '异常'}
                  tone={
                    healthy
                      ? { bg: platformColors.successBg, color: platformColors.success }
                      : { bg: platformColors.dangerBg, color: platformColors.danger }
                  }
                />
              </div>
            )}
            {slice.port != null && <div>Port: {slice.port}</div>}
            {slice.pid != null && <div>PID: {slice.pid}</div>}
          </div>
        )
      })}
    </div>
  )
}

/** Worker Runtime V2 — Runtime Version/Profile/OS/Arch, all sourced from
 * `resource_profile` (the register-time hardware profile — see
 * `WorkerHardwareProfile`). Renders "—" per-field, never as a block, when
 * an older Worker build hasn't reported one or more of them. */
function RuntimeProfileSummary({ profile }: { profile: AdminWorkerRow['resource_profile'] }) {
  if (!profile) return <span>—</span>
  const parts = [
    profile.profile ? `profile=${profile.profile}` : null,
    profile.os ? `${profile.os}${profile.arch ? `/${profile.arch}` : ''}` : null,
    profile.gpu_name ? String(profile.gpu_name) : null,
  ].filter(Boolean)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
      <span>v{profile.runtime_version ?? '—'}</span>
      {parts.length > 0 && <span style={{ color: platformColors.secondaryText }}>{parts.join(' · ')}</span>}
    </div>
  )
}

function CurrentTasksList({
  tasks,
  currentTask,
  t,
}: {
  tasks: AdminWorkerRow['current_tasks']
  currentTask: AdminWorkerRow['current_task']
  t: (key: string) => string
}) {
  const list = tasks && tasks.length ? tasks : currentTask ? [currentTask] : []
  if (!list.length) return <span>{t('workers.task.idle')}</span>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {list.map((task) => (
        <span key={task.job_id}>
          {task.job_type_label} #{task.job_id.slice(0, 8)}
          {task.tenant_name ? ` · ${task.tenant_name}` : ''}
        </span>
      ))}
    </div>
  )
}

/** 接单控制 — the one place that decides which admin actions are even
 * offered, driven entirely by real backend state (never by process-kill
 * semantics — spec §10: pausing/disabling only ever affects *future*
 * claims, `JobService.claim_for_worker` never touches an in-flight Job).
 *
 * - `disabled` → only [启用 Worker] (undoes disable).
 * - `draining` → [恢复接单] (undoes drain) + [禁用] (still reachable
 *   directly from draining, same as before this round).
 * - `accepting` (admin-enabled AND alive) → [暂停接单] (drain) + [禁用].
 * - `waiting_online` (admin-enabled but offline) → [禁用] ONLY — spec §8/
 *   §9: an offline Worker has nothing running to "drain", so no Drain
 *   action is ever offered while offline (Stop Condition #4). */
function WorkerAcceptControlActions({
  row,
  actioningId,
  size,
  onAction,
  t,
}: {
  row: { worker_id: string; name: string | null; status: string; online: boolean }
  actioningId: string | null
  size: 'small' | 'middle'
  onAction: (workerId: string, action: 'enable' | 'drain' | 'disable', successMessage: string) => void
  t: (key: string, vars?: Record<string, string>) => string
}) {
  const state = acceptingState(row.status, row.online)
  const loading = actioningId === row.worker_id
  const label = row.name || row.worker_id

  if (state === 'disabled') {
    return (
      <Button
        type="primary"
        size={size}
        icon={<PlayCircleOutlined />}
        loading={loading}
        onClick={() => onAction(row.worker_id, 'enable', t('workers.control.enableSuccess', { name: label }))}
      >
        {t('workers.control.enable')}
      </Button>
    )
  }

  if (state === 'draining') {
    return (
      <>
        <Button
          type="primary"
          size={size}
          icon={<PlayCircleOutlined />}
          loading={loading}
          onClick={() => onAction(row.worker_id, 'enable', t('workers.control.resumeSuccess', { name: label }))}
        >
          {t('workers.control.resume')}
        </Button>
        <Popconfirm
          title={t('workers.control.disableConfirmTitle')}
          description={
            <div>
              <div>{t('workers.control.workerLabel', { name: label })}</div>
              <div>{t('workers.control.disableConfirmDescription')}</div>
            </div>
          }
          okText={t('workers.control.disableConfirmOk')}
          cancelText={t('workers.control.cancel')}
          okButtonProps={{ danger: true }}
          onConfirm={() => onAction(row.worker_id, 'disable', t('workers.control.disableSuccess', { name: label }))}
        >
          <Button size={size} danger icon={<PoweroffOutlined />} loading={loading}>
            {t('workers.control.disable')}
          </Button>
        </Popconfirm>
      </>
    )
  }

  return (
    <>
      {state === 'accepting' && (
        <Popconfirm
          title={t('workers.control.drainConfirmTitle')}
          description={t('workers.control.drainConfirmDescription')}
          okText={t('workers.control.drainConfirmOk')}
          cancelText={t('workers.control.cancel')}
          onConfirm={() => onAction(row.worker_id, 'drain', t('workers.control.drainSuccess', { name: label }))}
        >
          <Button size={size} icon={<PauseCircleOutlined />} loading={loading}>
            {t('workers.control.drain')}
          </Button>
        </Popconfirm>
      )}
      <Popconfirm
        title={t('workers.control.disableConfirmTitle')}
        description={
          <div>
            <div>{t('workers.control.workerLabel', { name: label })}</div>
            <div>{t('workers.control.disableConfirmDescription')}</div>
          </div>
        }
        okText={t('workers.control.disableConfirmOk')}
        cancelText={t('workers.control.cancel')}
        okButtonProps={{ danger: true }}
        onConfirm={() => onAction(row.worker_id, 'disable', t('workers.control.disableSuccess', { name: label }))}
      >
        <Button size={size} danger icon={<PoweroffOutlined />} loading={loading}>
          {t('workers.control.disable')}
        </Button>
      </Popconfirm>
    </>
  )
}

/** Worker Engine Lifecycle Control Plane P0 — Actual State (spec 五's
 * exact seven states) rendered as a colored dot + label, independent of
 * (and never derived from) the admin-owned Desired State pill below it. */
function EngineActualStateBadge({ engine, t }: { engine: WorkerEngineRow; t: (key: string) => string }) {
  if (!engine.known || engine.actual_state == null) {
    return (
      <span style={{ color: platformColors.mutedText, fontWeight: 600, fontSize: 12 }}>
        ○ {t('workers.engine.state.unknown')}
      </span>
    )
  }
  const tone = engineActualStateTone(engine.actual_state, t) ?? { color: platformColors.mutedText, label: t('workers.engine.state.unloaded'), dot: '○' }
  const badge = (
    <span style={{ color: tone.color, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
      {tone.dot} {tone.label}
    </span>
  )
  return engine.last_error ? <Tooltip title={engine.last_error}>{badge}</Tooltip> : badge
}

function EngineReadinessPill({ readiness, t }: { readiness: WorkerEngineRow['readiness'] | undefined; t: (key: string) => string }) {
  if (!readiness) return <span style={{ color: platformColors.mutedText }}>—</span>
  const isError = engineReadinessIsError(readiness)
  const isIdle = engineReadinessIsIdle(readiness)
  const tone = isError
    ? { bg: platformColors.dangerBg, color: platformColors.danger }
    : isIdle
      ? { bg: 'var(--pf-neutral-bg)', color: platformColors.mutedText }
      : readiness === 'engine_starting' || readiness === 'draining'
        ? { bg: platformColors.infoBg, color: platformColors.info }
        : readiness === 'ready'
          ? { bg: platformColors.successBg, color: platformColors.success }
          : { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText }
  return <StatusPill label={engineReadinessLabel(readiness, t)} tone={tone} />
}

function EngineGpuTypeCell({ row }: { row: AdminWorkerRow }) {
  const label = inferGpuEngineLabel(row.worker_id, row.engine_lifecycle)
  return <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</span>
}

function EngineStatusCell({ summary, t }: { summary: WorkerEngineLifecycleSummary | undefined; t: (key: string) => string }) {
  if (!summary?.applicable) return <span style={{ color: platformColors.mutedText }}>—</span>
  if (!summary.engines.length) return <span style={{ color: platformColors.mutedText }}>—</span>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {summary.engines.map((engine) => (
        <div key={engine.engine_id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
          <EngineActualStateBadge engine={engine} t={t} />
        </div>
      ))}
    </div>
  )
}

function RuntimeUpgradeCell({ row, t }: { row: AdminWorkerRow; t: (key: string, opts?: Record<string, unknown>) => string }) {
  const sha = row.source_sha ? row.source_sha.slice(0, 7) : '—'
  const version = row.resource_profile?.runtime_version ?? row.version ?? '—'
  const badge = runtimeUpgradeBadgeMeta(row.runtime_upgrade?.status, t)
  const detail = formatRuntimeUpgradeDetail(row.runtime_upgrade ?? null, t)
  return (
    <div style={{ fontSize: 12, minWidth: 132, whiteSpace: 'nowrap' }}>
      <div>v{version}</div>
      <div style={{ color: platformColors.secondaryText }}>
        <Tooltip title={row.source_sha ?? undefined}>{t('workers.runtime.build')} {sha}</Tooltip>
      </div>
      <Tag color={badge.color} style={{ marginTop: 4 }} title={detail ?? undefined}>{badge.label}</Tag>
    </div>
  )
}

/** Worker Engine Lifecycle Control Plane P0 (spec 二十二) — per-engine
 * card: Installed/允许调度 (informational only, from the pre-existing
 * Capability Assignment matrix — never a second enablement checkbox) +
 * Desired/Actual State (this round's new axis) + [上线]/[下线]. */
function EngineLifecycleCard({
  engine,
  schedulingAllowed,
  actioning,
  onSetDesiredState,
  t,
}: {
  engine: WorkerEngineRow
  schedulingAllowed: boolean | null
  actioning: boolean
  onSetDesiredState: (engineId: string, desired: 'OFFLINE' | 'READY') => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const notInstalled = engine.installed === false
  const unknownInstalled = engine.installed === null
  const awaitingReport = !engine.known || engine.actual_state == null
  const wantsOffline = engine.desired_state !== 'READY'
  const usage = engine.resource_usage || {}
  const controlsDisabled = engineControlDisabled(engine, wantsOffline)
  const controlLoading = engineControlLoading(engine, wantsOffline, actioning)
  const controlLabel = formatEngineControlLabel(engine, wantsOffline, t)
  return (
    <div style={{ border: `1px solid ${platformColors.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{engine.display_name}</div>
          <div style={{ fontSize: 12, color: platformColors.secondaryText, marginTop: 2 }}>
            安装：{unknownInstalled ? '未知（尚无上报）' : notInstalled ? '未安装' : '已安装'}
            {' · '}
            允许调度：{schedulingAllowed === null ? '—' : schedulingAllowed ? '是' : '否'}
          </div>
        </div>
        <EngineActualStateBadge engine={engine} t={t} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: platformColors.secondaryText, marginTop: 8 }}>
        <span>{t('workers.engine.desired.onDemand')}: {desiredStateLabel(engine.desired_state, t)}</span>
        <span>{t('workers.engine.readiness.ready')}: <EngineReadinessPill readiness={engine.readiness} t={t} /></span>
        <span>显存：{usage.vram_mb != null ? `${(usage.vram_mb / 1024).toFixed(1)} GB` : awaitingReport || notInstalled ? '—' : '未知'}</span>
        <span>当前任务：{usage.active_task_count ?? (engine.actual_state === 'BUSY' ? '≥1' : 0)}</span>
      </div>
      {engine.last_error ? (
        <Alert type="error" showIcon style={{ marginTop: 8, fontSize: 12 }} message={engine.last_error} />
      ) : null}
      <div style={{ marginTop: 8 }}>
        {wantsOffline ? (
          <Tooltip
            title={
              awaitingReport
                ? '尚无 Worker 上报，请等待 Runtime 升级并完成心跳后再操作'
                : notInstalled
                  ? '该机器未安装此引擎，无法上线'
                  : undefined
            }
          >
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              loading={controlLoading}
              disabled={controlsDisabled}
              onClick={() => onSetDesiredState(engine.engine_id, 'READY')}
            >
              {controlLabel}
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title={awaitingReport ? '尚无 Worker 上报，请等待 Runtime 升级并完成心跳后再操作' : undefined}>
            <span>
              <Popconfirm
                title={t('workers.engine.control.confirmUnloadTitle', { name: engine.display_name })}
                description={t('workers.engine.control.confirmUnloadDescription')}
                okText={t('workers.engine.control.unload')}
                cancelText="取消"
                disabled={controlsDisabled}
                onConfirm={() => onSetDesiredState(engine.engine_id, 'OFFLINE')}
              >
                <Button size="small" danger icon={<PoweroffOutlined />} loading={controlLoading} disabled={controlsDisabled}>
                  {controlLabel}
                </Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

/** Worker Engine Lifecycle Control Plane P0 — Worker 详情抽屉里的「引擎运行时」
 * 区域，与上方保留不变的「任务能力分配」矩阵严格分离（spec 二/二十二）。
 * Polls independently at the same cadence as the rest of this page so an
 * Admin's 上线/下线 click's STARTING → WARMING → READY progression is
 * visible without a manual refresh. */
function EngineLifecycleSection({
  workerId,
  schedulingByType,
  listSummary,
  t,
}: {
  workerId: string
  schedulingByType: Record<string, boolean>
  listSummary?: WorkerEngineLifecycleSummary
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const [engines, setEngines] = useState<WorkerEngineRow[] | null>(listSummary?.engines ?? null)
  const [applicable, setApplicable] = useState<boolean | null>(listSummary?.applicable ?? null)
  const [activeEngineId, setActiveEngineId] = useState<string | null | undefined>(listSummary?.active_engine_id)
  const [loading, setLoading] = useState(true)
  const [actioningEngineId, setActioningEngineId] = useState<string | null>(null)

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    platformListWorkerEngines(workerId)
      .then((res) => {
        setApplicable(res.data.engine_lifecycle_applicable)
        setEngines(res.data.engines)
        const ready = res.data.engines.find((e) => e.actual_state === 'READY' || e.actual_state === 'BUSY')
        setActiveEngineId(ready?.engine_id ?? null)
      })
      .catch((err) => {
        if (!opts?.silent) message.error((err as Error).message || '加载引擎运行时状态失败')
      })
      .finally(() => {
        if (!opts?.silent) setLoading(false)
      })
  }, [workerId])

  useEffect(() => {
    load()
    const timer = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  const handleSetDesiredState = async (engineId: string, desired: 'OFFLINE' | 'READY') => {
    setActioningEngineId(engineId)
    try {
      await platformSetWorkerEngineDesiredState(workerId, engineId, desired)
      message.success(desired === 'READY' ? '已提交上线请求，正在启动/加载模型' : '已提交下线请求，将排空后停止')
      load({ silent: true })
    } catch (err) {
      message.error((err as Error).message || '操作失败')
    } finally {
      setActioningEngineId(null)
    }
  }

  if (applicable === false) {
    return null
  }

  return (
    <>
      <DetailSectionHeader>{t('workers.engine.sectionTitle')}</DetailSectionHeader>
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 8 }}>{t('workers.engine.sectionHint')}</div>
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 8 }}>
        {t('workers.engine.activeEngine')}: {summarizeActiveEngine({ applicable: applicable ?? false, active_engine_id: activeEngineId })}
      </div>
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 8 }}>{t('workers.engine.gpuSlotShared')}</div>
      {loading && !engines ? (
        <span style={{ fontSize: 12, color: platformColors.mutedText }}>加载中…</span>
      ) : !engines || engines.length === 0 ? (
        <span style={{ fontSize: 12, color: platformColors.mutedText }}>暂无可管理的 GPU Engine</span>
      ) : (
        engines.map((engine) => (
          <EngineLifecycleCard
            key={engine.engine_id}
            engine={engine}
            schedulingAllowed={schedulingByType[engine.engine_type] ?? null}
            actioning={actioningEngineId === engine.engine_id}
            onSetDesiredState={handleSetDesiredState}
            t={t}
          />
        ))
      )}
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', padding: '8px 0', borderBottom: `1px solid ${platformColors.border}`, fontSize: 13 }}>
      <div style={{ width: 120, color: platformColors.secondaryText, flexShrink: 0 }}>{label}</div>
      <div style={{ color: platformColors.primaryText, wordBreak: 'break-all' }}>{value}</div>
    </div>
  )
}

/** Worker Node Admin UI V1.1 §13 — the drawer's flat DetailRow list is
 * regrouped into named sections (基本信息/能力/运行情况/接单控制) so the
 * admin control actions never again sit as two bare unlabeled buttons at
 * the very bottom of an undifferentiated list. */
function DetailSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: platformColors.secondaryText, marginTop: 18, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {children}
    </div>
  )
}

/** VISTOERA_TASK_FAILURE_DIAGNOSTICS_WORKER_ELIGIBILITY_AND_ADMIN_
 * RECOVERY_GUIDANCE_V1 (spec §18) — 调度诊断/Troubleshooting section: Host /
 * Process / Desired / Accepting Jobs / Load already render above via the
 * existing rows; this adds only what spec §18 asks for that nothing else
 * on this page already shows — Last claim / Last successful task / Last
 * failed task (+ reason). Reads `lifecycle.troubleshooting`, which the
 * detail endpoint (`get_worker_detail_page`) already folds in on this
 * same on-demand fetch — never a second request. */
function WorkerTroubleshootingSection({ lifecycle }: { lifecycle?: WorkerProcessLifecycleOverlay | null }) {
  const snapshot = lifecycle?.troubleshooting
  if (!snapshot) return null
  const fmt = (ref: { task_id: string; job_type: string; at: string | null } | null) =>
    ref ? `${formatDateTime(ref.at)} · ${ref.job_type} (${ref.task_id.slice(0, 8)})` : '—'
  return (
    <>
      <DetailSectionHeader>调度诊断</DetailSectionHeader>
      <DetailRow label="Last claim" value={fmt(snapshot.last_claim)} />
      <DetailRow label="Last success" value={fmt(snapshot.last_success)} />
      <DetailRow
        label="Last failed"
        value={
          snapshot.last_failed ? (
            <span>
              {formatDateTime(snapshot.last_failed.at)} · {snapshot.last_failed.job_type} ({snapshot.last_failed.task_id.slice(0, 8)})
              <br />
              <span style={{ color: platformColors.danger }}>{snapshot.last_failed.label}</span>
            </span>
          ) : (
            '—'
          )
        }
      />
    </>
  )
}

/** Worker Enrollment Security (Worker Runtime V2, spec §21-§26) — the
 * Platform Admin side of "short-lived enrollment token -> one-time
 * enrollment -> per-Worker long-lived credential": create a token (shown
 * exactly once, spec §21), list outstanding ones, revoke one. This is
 * deliberately its own small self-contained section (not folded into the
 * Worker table itself — a token has no `worker_id` yet, it is not a
 * Worker row) per spec §54's "Kept deliberately minimal" precedent. */
/** VISTOERA_AI_CHAT_CONTROL_PLANE_P0 spec §10 — capability-level health
 * (READY/BUSY/DEGRADED/LOGIN_REQUIRED/UNAVAILABLE), *not* a PC1-specific
 * concept: any Worker capability may report this optional `status`
 * inside `capabilities_detail` (see `remote_workers.list_capabilities`),
 * this component just renders whatever it finds for the matching
 * capability id. A capability that never reports a `status` still gets
 * its existing binary healthy/unhealthy tag — no regression. */
const CAPABILITY_HEALTH_TONE: Record<string, { color: string; labelKey: string }> = {
  ready: { color: 'success', labelKey: 'workers.capabilityHealth.ready' },
  busy: { color: 'processing', labelKey: 'workers.capabilityHealth.busy' },
  degraded: { color: 'warning', labelKey: 'workers.capabilityHealth.degraded' },
  login_required: { color: 'error', labelKey: 'workers.capabilityHealth.loginRequired' },
  unavailable: { color: 'default', labelKey: 'workers.capabilityHealth.unavailable' },
}

function CapabilityAssignmentSummary({ row, t }: { row: AdminWorkerRow; t: (key: string) => string }) {
  const summary = row.capability_assignment
  const labels = summary?.capability_labels ?? {}
  const detailById = new Map((row.capabilities_detail ?? []).map((d) => [d.id, d]))
  if (!summary || summary.advertised_capabilities.length === 0) return <span>—</span>
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {summary.advertised_capabilities.map((cap) => {
        const enabled = summary.enabled_capabilities.includes(cap)
        const label = labels[cap] ?? cap
        const detail = detailById.get(cap)
        const healthStatus = detail?.status ? CAPABILITY_HEALTH_TONE[detail.status.toLowerCase()] : undefined
        const platforms = detail?.metadata?.extra?.platforms ?? []
        const showAggregateHealth = enabled && healthStatus && platforms.length === 0
        return (
          <Tooltip
            key={cap}
            title={
              detail?.health_detail ||
              (enabled ? t('workers.capability.adminEnabled') : t('workers.capability.adminDisabled'))
            }
          >
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2 }}>
              <Tag color={enabled ? 'success' : 'default'} style={{ fontSize: 11, margin: 0 }}>
                {label}
                {!enabled ? ` ${t('workers.capability.disabledSuffix')}` : ''}
              </Tag>
              {enabled &&
                platforms.map((platform) => {
                  const platformHealth = CAPABILITY_HEALTH_TONE[platform.status.toLowerCase()]
                  const platformLabel = platform.label || platform.id
                  const statusLabel = platformHealth ? t(platformHealth.labelKey) : platform.status
                  return (
                    <Tag
                      key={`${cap}-${platform.id}`}
                      color={platformHealth?.color ?? 'default'}
                      style={{ fontSize: 11, margin: 0 }}
                    >
                      {platformLabel} {statusLabel}
                    </Tag>
                  )
                })}
              {showAggregateHealth && (
                <Tag color={healthStatus.color} style={{ fontSize: 11, margin: 0 }}>
                  {t(healthStatus.labelKey)}
                </Tag>
              )}
            </span>
          </Tooltip>
        )
      })}
    </div>
  )
}

const PRODUCT_AI_DIALOGUE_COLUMN_ID = 'product.ai_dialogue'

function aiDialogueTooltip(breakdown: WorkerCapabilityMatrixWorker['ai_dialogue_breakdown']): string | undefined {
  if (!breakdown?.length) return undefined
  const lines = breakdown.map((b) => `${b.label}${b.enabled ? ' · 已启用' : ' · 未启用'}`)
  return `AI 对话\n支持：\n${lines.join('\n')}`
}

function WorkerArchiveDangerSection({
  workerId,
  displayName,
  onArchived,
}: {
  workerId: string
  displayName: string
  onArchived: () => void
}) {
  const [eligibility, setEligibility] = useState<WorkerDeletionEligibility | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    platformGetWorkerDeletionEligibility(workerId)
      .then((res) => setEligibility(res.data))
      .catch(() => setEligibility(null))
      .finally(() => setLoading(false))
  }, [workerId])

  useEffect(() => {
    load()
  }, [load])

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await platformArchiveWorker(workerId)
      message.success('Worker 已归档，将从活动列表与能力矩阵中移除')
      setConfirmOpen(false)
      onArchived()
    } catch (err) {
      message.error((err as Error).message || '归档失败')
      load()
    } finally {
      setArchiving(false)
    }
  }

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${platformColors.border}` }}>
      <DetailSectionHeader>危险操作</DetailSectionHeader>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        永久退役且机器已不存在时，可归档 Worker。历史任务与审计记录保留；归档后不再参与调度与能力矩阵。
      </Typography.Paragraph>
      {loading ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>正在检查删除条件…</Typography.Text>
      ) : eligibility ? (
        <>
          {!eligibility.allowed && eligibility.blockers.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8, fontSize: 12 }}
              message="当前不可归档"
              description={eligibility.blockers.map((b) => b.message).join('；')}
            />
          ) : null}
          <Button danger size="small" disabled={!eligibility.allowed} onClick={() => setConfirmOpen(true)}>
            删除 Worker
          </Button>
        </>
      ) : (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>无法加载归档条件</Typography.Text>
      )}
      <Modal
        title="确认归档 Worker"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleArchive}
        okText="确认归档"
        okButtonProps={{ danger: true, loading: archiving }}
        cancelText="取消"
      >
        <p>
          <strong>{displayName}</strong>
          <br />
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{workerId}</span>
        </p>
        <p style={{ fontSize: 12 }}>
          状态：{eligibility?.status ?? '—'}
          {eligibility?.online ? ' · 在线' : ''}
        </p>
        <p style={{ fontSize: 12, marginBottom: 0 }}>
          归档后将从 Worker 列表与任务能力分配矩阵中移除；历史任务与审计记录不会删除。
        </p>
      </Modal>
    </div>
  )
}

const DIRECT_TEST_JOB_TYPES = [
  'ai_chat_chatgpt',
  'ai_chat_gemini',
  'ai_chat_grok',
  'ai_chat_deepseek',
  'ai_chat_qwen',
  'ai_chat_doubao',
]
const DIRECT_TEST_POLL_MS = 3_000
const DIRECT_TEST_TERMINAL = new Set(['succeeded', 'failed', 'cancelled'])

/** VistoeraTASK-00570 — Admin-only Direct Test: one Job only this exact
 * Worker may claim, regardless of its capability assignments; it never
 * enters public routing. Polls only its own Job, only until terminal. */
function WorkerDirectTestSection({ workerId }: { workerId: string }) {
  const [jobType, setJobType] = useState(DIRECT_TEST_JOB_TYPES[0])
  const [prompt, setPrompt] = useState('请只回复 OK')
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<WorkerDirectTestCreated | null>(null)
  const [detail, setDetail] = useState<PlatformTaskDetail | null>(null)

  useEffect(() => {
    setCreated(null)
    setDetail(null)
  }, [workerId])

  useEffect(() => {
    if (!created) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = async () => {
      try {
        const next = (await platformGetTaskDetail(created.test_job_id)).data
        if (cancelled) return
        setDetail(next)
        if (DIRECT_TEST_TERMINAL.has(next.status)) return
      } catch {
        if (cancelled) return
      }
      timer = setTimeout(tick, DIRECT_TEST_POLL_MS)
    }
    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [created])

  const submit = async () => {
    setSubmitting(true)
    try {
      const res = await platformCreateWorkerTestJob(workerId, { job_type: jobType, prompt })
      setDetail(null)
      setCreated(res.data)
      message.success('Direct Test 已创建，等待该 Worker 领取')
    } catch (err) {
      message.error((err as Error).message || '创建 Direct Test 失败')
    } finally {
      setSubmitting(false)
    }
  }

  const resultText = extractDirectTestResultSummary(detail?.redacted_result)
  return (
    <div style={{ marginTop: 16 }}>
      <DetailSectionHeader>测试 Worker（Admin Direct Test）</DetailSectionHeader>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
        仅该 Worker 可领取；不修改 capabilities，不进入公开路由与默认 provider 选择。
      </Typography.Paragraph>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Select
          value={jobType}
          onChange={setJobType}
          options={DIRECT_TEST_JOB_TYPES.map((value) => ({ value, label: value }))}
          style={{ width: 240 }}
        />
        <Input.TextArea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={4000} />
        <div>
          <Button type="primary" loading={submitting} disabled={!prompt.trim()} onClick={submit}>
            测试 Worker
          </Button>
        </div>
      </div>
      {created && (
        <div style={{ marginTop: 12 }}>
          {!created.worker_advertises_job_type && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message="该 Worker 未声明支持此 job_type，Worker 端可能直接返回不支持的任务类型"
            />
          )}
          <DetailRow
            label="TEST JOB ID"
            value={
              <Typography.Text copyable style={{ fontFamily: 'monospace' }}>
                {created.test_job_id}
              </Typography.Text>
            }
          />
          <DetailRow label="TARGET WORKER" value={created.target_worker_id} />
          <DetailRow
            label="ACTUAL WORKER"
            value={
              detail?.worker?.worker_id
                ? `${detail.worker.worker_name || detail.worker.display || detail.worker.worker_id} (${detail.worker.worker_id})`
                : '尚未领取'
            }
          />
          <DetailRow label="JOB TYPE" value={created.job_type} />
          <DetailRow
            label="STATUS"
            value={
              <span>
                <Tag color="purple">ADMIN TEST</Tag>
                {detail?.status_label || detail?.status || created.status}
              </span>
            }
          />
          <DetailRow label="CREATED AT" value={formatDateTime(detail?.timeline?.created_at ?? created.created_at)} />
          <DetailRow label="CLAIMED AT" value={formatDateTime(detail?.timeline?.assigned_at ?? detail?.timeline?.started_at)} />
          <DetailRow label="FINISHED AT" value={formatDateTime(detail?.timeline?.ended_at)} />
          <DetailRow
            label="RESULT SUMMARY"
            value={resultText ? resultText.slice(0, 500) : '—'}
          />
          <DetailRow label="ERROR CODE" value={detail?.error?.error_code ?? '—'} />
          <DetailRow label="ERROR MESSAGE" value={detail?.error?.error_detail ?? '—'} />
        </div>
      )}
    </div>
  )
}

function WorkerCapabilityMatrixSection({ onChanged }: { onChanged?: () => void }) {
  const [matrix, setMatrix] = useState<WorkerCapabilityMatrixResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    platformGetWorkerCapabilityMatrix()
      .then((res) => setMatrix(res.data))
      .catch((err) => {
        if (!opts?.silent) message.error((err as Error).message || '加载能力矩阵失败')
      })
      .finally(() => {
        if (!opts?.silent) setLoading(false)
      })
  }, [])

  useEffect(() => {
    // VISTOERA_PC4_REFERENCE_CAPTURE_ADMIN_ENABLE_GATE_FORENSIC_P0 §10 —
    // the matrix previously loaded once at mount only, so a Worker that
    // restarted with a newly advertised capability (e.g. PC4 restarting
    // with `reference.capture.douyin`) stayed invisible in an already-open
    // Admin page forever: `cell.advertised` kept showing the stale
    // snapshot, the checkbox rendered as an unclickable "—", and the
    // only remedies were a manual page reload or the section's own
    // "刷新" button. Poll silently at the same cadence as the page-level
    // worker list refresh so capability state converges without any
    // admin action (no loading flicker, no error toasts on transient
    // poll failures — a failed silent poll just retries next tick).
    load()
    const timer = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  const onToggle = async (workerId: string, capabilityId: string, enabled: boolean) => {
    const key = `${workerId}:${capabilityId}`
    setSavingKey(key)
    try {
      const row = matrix?.workers?.find((w) => w.worker_id === workerId)
      const targets =
        capabilityId === PRODUCT_AI_DIALOGUE_COLUMN_ID
          ? (row?.ai_dialogue_breakdown ?? []).filter((b) => b.advertised).map((b) => b.capability_id)
          : [capabilityId]
      let anyChanged = false
      for (const capId of targets) {
        const res = await platformSetWorkerCapabilityAssignment(workerId, capId, enabled)
        if (res.data.changed) anyChanged = true
      }
      if (anyChanged) {
        message.success('能力分配已更新')
        load()
        onChanged?.()
      }
    } catch (err) {
      message.error((err as Error).message || '更新失败')
    } finally {
      setSavingKey(null)
    }
  }

  const caps = matrix?.capabilities ?? []
  const workers = matrix?.workers ?? []

  return (
    <SectionCard
      title="任务能力分配"
      allowOverflow
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>刷新</Button>}
    >
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 8 }}>
        真实调度 = Worker 声明能力 ∩ 管理员启用。未声明的能力显示为 —，不可勾选。
      </div>
      <Table
        className="pf-data-table pf-capability-matrix"
        rowKey="worker_id"
        loading={loading}
        dataSource={workers}
        pagination={false}
        scroll={{ x: true }}
        sticky
        size="small"
        locale={{ emptyText: '暂无 Worker' }}
        columns={[
          {
            title: 'Worker',
            dataIndex: 'name',
            fixed: 'left',
            width: 200,
            render: (v: string, row: WorkerCapabilityMatrixWorker) => (
              <div style={{ lineHeight: 1.25 }}>
                <div style={{ fontWeight: 600 }}>{v || row.worker_id}</div>
                {v && v !== row.worker_id ? (
                  <div style={{ color: platformColors.mutedText, fontSize: 11, fontFamily: 'monospace' }}>{row.worker_id}</div>
                ) : null}
              </div>
            ),
          },
          ...caps.map((cap) => ({
            title: cap.label,
            key: cap.id,
            width: 120,
            // Item 二十/二十一 — three visually-distinct states: dash cell
            // gets a muted background (`pf-cap-not-declared`) so it never
            // looks like merely "an empty checkbox slot"; a declared-but-
            // disabled checkbox instead gets a stronger border via the
            // `.pf-capability-matrix` CSS rule below.
            onCell: (row: (typeof workers)[number]) => ({
              className: row.assignments[cap.id]?.advertised ? undefined : 'pf-cap-not-declared',
            }),
            render: (_: unknown, row: (typeof workers)[number]) => {
              const cell = row.assignments[cap.id]
              if (!cell?.advertised) {
                return (
                  <div className="pf-cap-matrix-cell">
                    <Tooltip title="该 Worker 当前未声明此能力（未声明，不可勾选）">
                      <span style={{ color: platformColors.mutedText, fontWeight: 600 }}>—</span>
                    </Tooltip>
                  </div>
                )
              }
              const busy = savingKey === `${row.worker_id}:${cap.id}`
              const providerTip =
                cap.id === PRODUCT_AI_DIALOGUE_COLUMN_ID ? aiDialogueTooltip(row.ai_dialogue_breakdown) : undefined
              const baseTip = cell.enabled
                ? '已启用：管理员已允许调度'
                : cell.partial
                  ? '部分 Provider 已启用：点击可统一启用/关闭'
                  : '已声明但未启用：点击勾选以允许调度'
              return (
                <div className="pf-cap-matrix-cell">
                  <Tooltip title={providerTip ? `${baseTip}\n\n${providerTip}` : baseTip}>
                    <Checkbox
                      checked={cell.enabled}
                      indeterminate={Boolean(cell.partial)}
                      disabled={busy}
                      onChange={(e) => onToggle(row.worker_id, cap.id, e.target.checked)}
                    />
                  </Tooltip>
                </div>
              )
            },
          })),
        ]}
      />
    </SectionCard>
  )
}

function EnrollmentTokensSection() {
  const [tokens, setTokens] = useState<EnrollmentTokenRow[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [resultToken, setResultToken] = useState<string | null>(null)
  const [form] = Form.useForm()

  const load = useCallback(() => {
    setLoading(true)
    platformListEnrollmentTokens()
      .then((res) => setTokens(res.data.tokens))
      .catch((err) => message.error((err as Error).message || '加载 Enrollment Token 列表失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleCreate = async () => {
    const values = await form.validateFields()
    setCreating(true)
    try {
      const res = await platformCreateEnrollmentToken({
        label: values.label || undefined,
        ttl_seconds: (values.ttl_hours || 24) * 3600,
        max_uses: values.max_uses || 1,
        environment: values.environment || 'preview',
      })
      setResultToken(res.data.enrollment_token)
      load()
    } catch (err) {
      message.error((err as Error).message || '创建 Enrollment Token 失败')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: string) => {
    try {
      await platformRevokeEnrollmentToken(id)
      message.success('已撤销')
      load()
    } catch (err) {
      message.error((err as Error).message || '撤销失败')
    }
  }

  return (
    <SectionCard
      title="Worker Enrollment Tokens"
      extra={
        <Button size="small" type="primary" icon={<KeyOutlined />} onClick={() => { setResultToken(null); form.resetFields(); setCreateOpen(true) }}>
          创建 Enrollment Token
        </Button>
      }
    >
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 8 }}>
        新机器安装 Vistoera Worker Runtime 时，使用这里创建的 enrollment token 一次性兑换为该 Worker 自己独立的长期
        credential（spec §21-§26）——不需要发放共享的 WORKER_API_TOKENS。
      </div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="这里只用于全局计算 Worker 节点（PC1/PC3 等），与租户的「本地发布设备」（PC4 / Local Publisher Agent）是完全不同的两套凭证"
        description="为具体某个工作区添加本地发布设备（Douyin 等平台发布用），请让该租户在其工作区内的「本地发布设备」页面自行生成配对码，不要把这里的 enrollment token 交给它——两者的 token 表/哈希命名空间不通用，用错会得到「配对码无效或已过期」。"
      />
      <Table<EnrollmentTokenRow>
        className="pf-data-table"
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={tokens}
        pagination={false}
        sticky
        locale={{ emptyText: '暂无 Enrollment Token' }}
        columns={[
          { title: 'Label', dataIndex: 'label', align: 'left' as const, render: (v: string | null) => v ?? '—' },
          { title: '环境', dataIndex: 'environment' },
          { title: '过期时间', dataIndex: 'expires_at', render: (v: string | null) => formatDateTime(v) },
          { title: '使用次数', key: 'uses', render: (_: unknown, r: EnrollmentTokenRow) => `${r.use_count} / ${r.max_uses}` },
          { title: '最近使用 Worker', dataIndex: 'last_used_by_worker_id', render: (v: string | null) => v ?? '—' },
          {
            title: '状态',
            key: 'state',
            render: (_: unknown, r: EnrollmentTokenRow) => {
              const expired = new Date(r.expires_at || '').getTime() < Date.now()
              if (r.revoked) return <Tag color="default">已撤销</Tag>
              if (expired) return <Tag color="default">已过期</Tag>
              if (r.use_count >= r.max_uses) return <Tag color="default">已用完</Tag>
              return <Tag color="success">可用</Tag>
            },
          },
          {
            title: '操作',
            key: 'actions',
            render: (_: unknown, r: EnrollmentTokenRow) =>
              !r.revoked ? (
                <Popconfirm title="确认撤销该 enrollment token？" onConfirm={() => handleRevoke(r.id)}>
                  <Button size="small" danger>
                    撤销
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]}
      />
      <Modal
        title="创建 Enrollment Token"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={
          resultToken
            ? [
                <Button key="close" type="primary" onClick={() => setCreateOpen(false)}>
                  完成
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setCreateOpen(false)}>
                  取消
                </Button>,
                <Button key="create" type="primary" loading={creating} onClick={handleCreate}>
                  创建
                </Button>,
              ]
        }
      >
        {resultToken ? (
          <div>
            <Typography.Paragraph type="warning">
              请立即复制以下 enrollment token —— 关闭本窗口后将无法再次查看（后端只保存其哈希）。
            </Typography.Paragraph>
            <Input.TextArea value={resultToken} readOnly autoSize style={{ fontFamily: 'monospace', fontSize: 12 }} onFocus={(e) => e.target.select()} />
          </div>
        ) : (
          <Form form={form} layout="vertical" initialValues={{ ttl_hours: 24, max_uses: 1, environment: 'preview' }}>
            <Form.Item name="label" label="Label（例如：PC3-mac-media-worker）">
              <Input placeholder="可选，方便在列表中识别" />
            </Form.Item>
            <Form.Item name="ttl_hours" label="有效期（小时）">
              <InputNumber min={1} max={720} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="max_uses" label="最大使用次数">
              <InputNumber min={1} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="environment" label="环境">
              <Select options={[{ value: 'preview', label: 'preview' }, { value: 'production', label: 'production' }]} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </SectionCard>
  )
}

export default function PlatformWorkersPage() {
  const { t } = useTranslation('platformAdmin')
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<WorkersPageResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [actioningId, setActioningId] = useState<string | null>(null)
  // VISTOERA_TASK_FAILURE_DIAGNOSTICS_WORKER_ELIGIBILITY_AND_ADMIN_
  // RECOVERY_GUIDANCE_V1 (spec §13/§18) — deep-link support so a Task
  // Control Center Diagnostic Drawer's "[查看 Worker]" action can land
  // directly on this Worker's own detail drawer (`?worker=<id>`) instead
  // of only ever landing on the unfiltered list.
  const [drawerWorkerId, setDrawerWorkerId] = useState<string | null>(() => searchParams.get('worker'))
  const [drawerDetail, setDrawerDetail] = useState<WorkerDetail | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null)
  const consecutiveFailuresRef = useRef(0)
  const hasDataRef = useRef(false)

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setRefreshing(true)
    platformListWorkers()
      .then((res) => {
        consecutiveFailuresRef.current = 0
        hasDataRef.current = true
        setData(res.data)
        setError(null)
        setLastRefreshedAt(new Date())
      })
      .catch((err) => {
        consecutiveFailuresRef.current += 1
        // Platform Admin Density & Worker Liveness P0 (item 十八) — same
        // fix as OverviewPage.tsx: never let one transient poll failure
        // hide (or contradict) data already on screen; only the first
        // load, or `CONSECUTIVE_FAILURES_BEFORE_BANNER` in a row, surface
        // the banner. Existing good data is never cleared by a failed
        // poll.
        if (!hasDataRef.current || consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_BEFORE_BANNER) {
          setError(err.message || '数据加载失败')
        }
      })
      .finally(() => {
        setLoading(false)
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    load()
    intervalRef.current = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [load])

  const loadDrawerDetail = useCallback((workerId: string) => {
    setDrawerLoading(true)
    platformGetWorker(workerId)
      .then((res) => setDrawerDetail(res.data))
      .catch((err) => message.error((err as Error).message || '加载 Worker 详情失败'))
      .finally(() => setDrawerLoading(false))
  }, [])

  useEffect(() => {
    if (drawerWorkerId) loadDrawerDetail(drawerWorkerId)
    else setDrawerDetail(null)
  }, [drawerWorkerId, loadDrawerDetail])

  const openDrawer = useCallback(
    (workerId: string) => {
      setDrawerWorkerId(workerId)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('worker', workerId)
        return next
      })
    },
    [setSearchParams],
  )
  const closeDrawer = useCallback(() => {
    setDrawerWorkerId(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('worker')
      return next
    })
  }, [setSearchParams])

  const runAction = async (workerId: string, action: 'enable' | 'drain' | 'disable', successMessage: string) => {
    setActioningId(workerId)
    try {
      const fn = action === 'enable' ? platformEnableWorker : action === 'drain' ? platformDrainWorker : platformDisableWorker
      await fn(workerId)
      message.success(successMessage)
      // Worker Node Admin UI V1.1 §16 — every real state mutation this
      // page can make (accept-control action here, capability toggle in
      // `WorkerCapabilityMatrixSection`) re-fetches canonical state for
      // every surface it could affect: the list, the open detail drawer
      // (if any), and — via the list response's own `summary` block —
      // the top "在线 Worker" counters. Never asks the admin to reload
      // the page to see the effect of their own click.
      load({ silent: true })
      if (drawerWorkerId === workerId) loadDrawerDetail(workerId)
    } catch (err) {
      message.error(`操作失败：${(err as Error).message || '未知错误'}`)
    } finally {
      setActioningId(null)
    }
  }

  const summary = data?.summary
  const showSkeleton = loading && !data

  return (
    <div style={{ maxWidth: 1440 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>Worker 节点</div>
          <div style={{ fontSize: 12, lineHeight: '16px', color: platformColors.secondaryText, marginTop: 2 }}>
            Vistoera Worker Runtime — 真实注册/心跳/领取任务的分布式计算节点（Preview）
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: platformColors.mutedText }}>
            自动刷新 10s{lastRefreshedAt ? ` · 上次刷新 ${lastRefreshedAt.toLocaleTimeString('zh-CN', { hour12: false })}` : ''}
          </span>
          <button
            type="button"
            onClick={() => load()}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${platformColors.border}`,
              background: '#fff',
              color: platformColors.primaryText,
              fontSize: 12,
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            <ReloadOutlined spin={refreshing} /> 刷新数据
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.dangerBg}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          <span>
            数据加载失败：{error}
            {lastRefreshedAt && ` · 当前显示上次成功数据（${lastRefreshedAt.toLocaleTimeString('zh-CN', { hour12: false })}）`}
          </span>
          <button type="button" onClick={() => load()} style={{ background: 'transparent', border: 'none', color: platformColors.danger, fontWeight: 600, cursor: 'pointer' }}>
            重试
          </button>
        </div>
      )}

      {summary?.auto_process_paused && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: platformColors.warningBg,
            color: platformColors.warning,
            border: `1px solid ${platformColors.warning}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          自动处理已全局暂停（auto_process_paused）—— 所有租户的 image.pipeline 任务都不会被任何 Worker 领取，即使下方显示
          Online。如非有意为之，请通过 `/chains/auto-process/resume` 恢复。
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <SummaryCard title="Worker 总数" loading={showSkeleton} value={summary?.workers_total ?? '—'} iconBg={cardIconBg.worker} icon={<ClusterOutlined style={{ color: '#7C3AED' }} />} />
        <SummaryCard title="在线 Worker" loading={showSkeleton} value={summary?.workers_online ?? '—'} iconBg={cardIconBg.worker} icon={<ClusterOutlined style={{ color: platformColors.success }} />} />
        <SummaryCard
          title="总并发使用"
          loading={showSkeleton}
          value={summary ? `${summary.concurrency_used ?? '—'} / ${summary.concurrency_max ?? '—'}`  : '—'}
          hint="使用中 / 上限（按 Worker 上报的资源槽位汇总）"
          iconBg={cardIconBg.task}
          icon={<ClusterOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard title="当前运行任务" loading={showSkeleton} value={summary?.tasks_running ?? '—'} iconBg={cardIconBg.task} icon={<ClusterOutlined style={{ color: platformColors.warning }} />} />
      </div>

      <div style={{ marginTop: 10 }}>
        <WorkerCapabilityMatrixSection onChanged={() => load({ silent: true })} />
      </div>

      <SectionCard title={t('workers.workerList')} allowOverflow>
        <Table<AdminWorkerRow>
          className="pf-data-table"
          rowKey="worker_id"
          loading={showSkeleton}
          dataSource={data?.workers ?? []}
          pagination={false}
          scroll={{ x: 1740 }}
          sticky
          size="small"
          locale={{ emptyText: t('workers.emptyWorkers') }}
          onRow={(row) => ({ onClick: () => openDrawer(row.worker_id), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: t('workers.columns.name'),
              dataIndex: 'name',
              align: 'left' as const,
              width: 220,
              fixed: 'left' as const,
              // VistoeraTASK-00675 — display_name is primary; stable worker_id is secondary.
              render: (v: string | null, row: AdminWorkerRow) => (
                <div style={{ lineHeight: 1.25 }}>
                  <div style={{ fontWeight: 600 }}>{v || row.worker_id}</div>
                  <div style={{ color: platformColors.mutedText, fontSize: 11, fontFamily: 'monospace' }}>{row.worker_id}</div>
                </div>
              ),
            },
            {
              title: t('workers.columns.runningStatus'),
              key: 'running_status',
              width: 96,
              render: (_: unknown, row: AdminWorkerRow) => (
                <RunningStatusPill online={row.online} heartbeatAgeSeconds={row.heartbeat_age_seconds} t={t} />
              ),
            },
            {
              title: t('workers.columns.admissionStatus'),
              key: 'accepting_status',
              width: 96,
              render: (_: unknown, row: AdminWorkerRow) => <AcceptingStatusPill status={row.status} online={row.online} t={t} />,
            },
            {
              title: t('workers.columns.capabilities'),
              key: 'capability_assignment',
              align: 'left' as const,
              width: 140,
              render: (_: unknown, row: AdminWorkerRow) => <CapabilityAssignmentSummary row={row} t={t} />,
            },
            {
              title: t('workers.columns.gpuEngine'),
              key: 'engine_gpu_type',
              align: 'left' as const,
              width: 96,
              render: (_: unknown, row: AdminWorkerRow) => <EngineGpuTypeCell row={row} />,
            },
            {
              title: t('workers.columns.engineStatus'),
              key: 'engine_status',
              align: 'left' as const,
              width: 120,
              render: (_: unknown, row: AdminWorkerRow) => <EngineStatusCell summary={row.engine_lifecycle} t={t} />,
            },
            {
              title: t('workers.columns.runtimeEnv'),
              key: 'runtime_env',
              align: 'left' as const,
              width: 140,
              render: (_: unknown, row: AdminWorkerRow) => <RuntimeProfileSummary profile={row.resource_profile} />,
            },
            {
              title: t('workers.columns.concurrency'),
              key: 'concurrency',
              align: 'left' as const,
              width: 120,
              render: (_: unknown, row: AdminWorkerRow) => {
                // VistoeraTASK-00247 — the previous headline number here
                // summed every declared resource pool together (video_gpu
                // + image_gpu + cpu + deepzoom capacity, e.g. "1/5"),
                // which reads exactly like "5-way video concurrency" at a
                // glance even though the column header is generic "并发"
                // — misleading during a real video-incident triage. A
                // Worker that declares a `video_gpu` pool at all gets that
                // pool's own count as the headline instead; the full
                // per-pool breakdown (already correct) stays right below
                // via `ResourcePoolsSummary`. A Worker with no video_gpu
                // pool (e.g. a deepzoom-only machine) is completely
                // unaffected — same fallback as before.
                const videoPool = row.resource_snapshot?.pools?.video_gpu
                const headline = videoPool
                  ? `Video ${videoPool.in_use}/${videoPool.capacity}`
                  : row.concurrency_max !== null
                    ? `${row.concurrency_used ?? 0} / ${row.concurrency_max}`
                    : row.current_task
                      ? '1 / —'
                      : '0 / —'
                return (
                  <div>
                    <div>{headline}</div>
                    <div style={{ marginTop: 4 }}>
                      <ResourcePoolsSummary snapshot={row.resource_snapshot} />
                    </div>
                  </div>
                )
              },
            },
            {
              title: t('workers.columns.currentTask'),
              key: 'current_task',
              align: 'left' as const,
              width: 180,
              render: (_: unknown, row: AdminWorkerRow) => (
                <div>
                  <TaskStatePill row={row} t={t} />
                  <div style={{ marginTop: 4 }}>
                    <CurrentTasksList tasks={row.current_tasks} currentTask={row.current_task} t={t} />
                  </div>
                </div>
              ),
            },
            {
              title: t('workers.columns.lastHeartbeat'),
              dataIndex: 'last_seen_at',
              width: 108,
              render: (v: string | null, row: AdminWorkerRow) => {
                const age = row.heartbeat_age_seconds
                const label = formatRelativeTime(v, t)
                const content = <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
                if (!row.online && age != null && age > 60) {
                  return (
                    <Tooltip title={t('workers.heartbeat.staleTooltip', { seconds: Math.round(age) })}>
                      <span style={{ color: platformColors.danger, whiteSpace: 'nowrap' }}>{label}</span>
                    </Tooltip>
                  )
                }
                return <Tooltip title={formatDateTime(v)}>{content}</Tooltip>
              },
            },
            {
              title: t('workers.columns.runtime'),
              key: 'runtime_upgrade',
              width: 148,
              render: (_: unknown, row: AdminWorkerRow) => <RuntimeUpgradeCell row={row} t={t} />,
            },
            {
              title: t('workers.lifecycle.columnTitle'),
              key: 'lifecycle',
              width: 260,
              render: (_: unknown, row: AdminWorkerRow) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <LifecycleDesiredControl row={row} actioningId={actioningId} onChanged={() => load({ silent: true })} t={t} />
                </div>
              ),
            },
            {
              title: t('workers.columns.actions'),
              key: 'actions',
              width: 160,
              render: (_: unknown, row: AdminWorkerRow) => (
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <WorkerAcceptControlActions row={row} actioningId={actioningId} size="small" onAction={runAction} t={t} />
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <EnrollmentTokensSection />

      <Drawer title={drawerWorkerId ? `${t('workers.detail.title')} — ${drawerWorkerId}` : t('workers.detail.title')} open={!!drawerWorkerId} onClose={closeDrawer} width={460} loading={drawerLoading}>
        {drawerDetail && (
          <div>
            <DetailSectionHeader>{t('workers.detail.basic')}</DetailSectionHeader>
            <DetailRow label={t('workers.columns.name')} value={drawerDetail.name || drawerDetail.worker_id} />
            <DetailRow
              label="Worker ID"
              value={
                <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {drawerDetail.worker_id}
                </Typography.Text>
              }
            />
            {drawerDetail.worker_independence && (
              <>
                <DetailSectionHeader>Worker Identity（Foundation）</DetailSectionHeader>
                <DetailRow label="Device" value={drawerDetail.worker_independence.device_id ?? '—'} />
                <DetailRow label="Worker Software" value={drawerDetail.worker_independence.worker_software_id ?? '—'} />
                <DetailRow
                  label="Worker Release"
                  value={
                    drawerDetail.worker_independence.worker_release?.worker_release_id
                      ? `${drawerDetail.worker_independence.worker_release.worker_release_id} (${drawerDetail.worker_independence.worker_release.release_status ?? '—'})`
                      : `Legacy / ${drawerDetail.worker_independence.worker_release?.release_status ?? 'Unknown'}`
                  }
                />
                <DetailRow label="Runtime ID" value={drawerDetail.worker_independence.runtime_id ?? '—'} />
                <DetailRow
                  label="Connector"
                  value={
                    drawerDetail.worker_independence.connector
                      ? `${drawerDetail.worker_independence.connector.connector_id ?? '—'} · ${drawerDetail.worker_independence.connector.control_plane_environment ?? '—'}`
                      : '—'
                  }
                />
                <DetailRow
                  label="Contract"
                  value={drawerDetail.worker_independence.contract_compatibility?.status ?? 'UNKNOWN'}
                />
              </>
            )}
            <DetailRow label={t('workers.columns.name')} value={drawerDetail.name ?? '—'} />
            <DetailRow label={t('workers.columns.runningStatus')} value={<RunningStatusPill online={drawerDetail.online} heartbeatAgeSeconds={drawerDetail.heartbeat_age_seconds} t={t} />} />
            <DetailRow label={t('workers.detail.lastHeartbeat')} value={formatDateTime(drawerDetail.last_seen_at)} />
            <DetailRow label={t('workers.detail.registeredAt')} value={formatDateTime(drawerDetail.registered_at)} />

            <DetailSectionHeader>{t('workers.detail.scheduling')}</DetailSectionHeader>
            <DetailRow label={t('workers.columns.admissionStatus')} value={<AcceptingStatusPill status={drawerDetail.status} online={drawerDetail.online} t={t} />} />
            <DetailRow label={t('workers.lifecycle.desired')} value={drawerDetail.lifecycle?.desired_state ?? '—'} />
            <DetailRow label={t('workers.lifecycle.runtime')} value={<LifecycleRuntimePill lifecycle={drawerDetail.lifecycle} t={t} />} />
            <DetailRow label={t('workers.lifecycle.lastError')} value={drawerDetail.lifecycle?.last_error_code ? `${drawerDetail.lifecycle.last_error_code}${drawerDetail.lifecycle.last_error_message ? ` — ${drawerDetail.lifecycle.last_error_message}` : ''}` : '—'} />
            <DetailRow
              label={t('workers.lifecycle.columnTitle')}
              value={
                <div onClick={(e) => e.stopPropagation()}>
                  <LifecycleDesiredControl row={drawerDetail} actioningId={actioningId} onChanged={() => { load({ silent: true }); if (drawerWorkerId) loadDrawerDetail(drawerWorkerId) }} t={t} />
                </div>
              }
            />
            <DetailRow label={t('workers.columns.capabilities')} value={drawerDetail.capability_assignment?.advertised_capabilities?.length ? <CapabilityAssignmentSummary row={drawerDetail} t={t} /> : '—'} />

            <WorkerTroubleshootingSection lifecycle={drawerDetail.lifecycle} />

            <EngineLifecycleSection
              workerId={drawerDetail.worker_id}
              listSummary={drawerDetail.engine_lifecycle}
              schedulingByType={Object.fromEntries(
                (drawerDetail.capability_assignment?.advertised_capabilities ?? []).map((cap) => [
                  cap,
                  drawerDetail.capability_assignment?.enabled_capabilities.includes(cap) ?? false,
                ]),
              )}
              t={t}
            />

            <DetailSectionHeader>{t('workers.detail.operations')}</DetailSectionHeader>
            <DetailRow label={t('workers.columns.currentTask')} value={<CurrentTasksList tasks={drawerDetail.current_tasks} currentTask={drawerDetail.current_task} t={t} />} />
            <DetailRow
              label={t('workers.columns.concurrency')}
              value={
                drawerDetail.concurrency_max !== null ? (
                  <Tooltip title={`${drawerDetail.concurrency_used ?? 0} / ${drawerDetail.concurrency_max} = 当前正在执行 ${drawerDetail.concurrency_used ?? 0} 个任务，最大并发 ${drawerDetail.concurrency_max}。仅反映当前占用，不代表该 Worker 是否曾经领取过任务（历史 claim 情况见下方「调度诊断」）。`}>
                    <span style={{ cursor: 'help' }}>{`${drawerDetail.concurrency_used ?? 0} / ${drawerDetail.concurrency_max}`}</span>
                  </Tooltip>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow label="资源槽位" value={<ResourcePoolsSummary snapshot={drawerDetail.resource_snapshot} />} />

            <DetailSectionHeader>{t('workers.detail.runtime')}</DetailSectionHeader>
            <DetailRow label={t('workers.columns.runtimeEnv')} value={<RuntimeProfileSummary profile={drawerDetail.resource_profile} />} />
            <DetailRow
              label={t('workers.detail.buildSha')}
              value={
                <Tooltip title="Diagnostic only — not used for Release/Contract compatibility (TASK-00289).">
                  <span>{drawerDetail.source_sha ?? '—'}</span>
                </Tooltip>
              }
            />
            <DetailRow
              label={t('workers.detail.upgradeStatus')}
              value={<RuntimeUpgradeCell row={drawerDetail} t={t} />}
            />
            <DetailRow label="Runtime Components" value={<RuntimeComponentsSummary components={drawerDetail.runtime_components} />} />

            <DetailSectionHeader>{t('workers.detail.admissionControl')}</DetailSectionHeader>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <WorkerAcceptControlActions row={drawerDetail} actioningId={actioningId} size="middle" onAction={runAction} t={t} />
            </div>

            {!drawerDetail.archived && <WorkerDirectTestSection workerId={drawerDetail.worker_id} />}

            {!drawerDetail.archived && (
              <WorkerArchiveDangerSection
                workerId={drawerDetail.worker_id}
                displayName={drawerDetail.name || drawerDetail.worker_id}
                onArchived={() => {
                  closeDrawer()
                  load({ silent: true })
                }}
              />
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}
